import React from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useSettings } from "../../context/SettingsContext";
import { addToCart, decreaseCart, incrementCart, removeFromCart } from "../../store/reducers/cartSlice";
import imgUrl from "../../imgUrl";
import { useTranslation } from "react-i18next";

function CardList({ data }) {
  const { currencySymbol } = useSettings();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cart = useSelector((state) => state.cart);
  const CartItem = cart.cartItems.find((item) => item.id === data?.id);
  const { t } = useTranslation();

  const handleAddToCart = () => {
    if (data.has_variants) {
      navigate(`/product/${data.slug}`);
    } else {
      dispatch(addToCart(data));
    }
  };

  const handleDecrease = () => {
    if (CartItem?.cartQuantity === 1) {
      dispatch(removeFromCart(data));
    } else {
      dispatch(decreaseCart(data));
    }
  };

  return (
    <>
      <div className="group flex bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden">
        {/* Image */}
        <div
          onClick={() => navigate(`/product/${data.slug}`)}
          className="relative flex-shrink-0 w-32 sm:w-44 h-32 sm:h-44 cursor-pointer overflow-hidden bg-white"
        >
          {data?.discount_percentage > 0 && (
            <span className="absolute top-2 left-2 z-10 bg-orange-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
              {Math.ceil(data.discount_percentage)}% Off
            </span>
          )}
          {data?.stock_quantity === 0 && (
            <span className="absolute top-2 right-2 z-10 bg-red-100 text-red-600 text-xs font-semibold px-2 py-0.5 rounded-full">
              {t('card.outOfStock')}
            </span>
          )}
          <img
            src={`${imgUrl}/${data?.main_image}`}
            alt={data?.name}
            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
            onError={e => { e.target.src = "https://via.placeholder.com/160"; }}
          />
        </div>

        {/* Info */}
        <div className="flex-1 p-4 flex flex-col justify-between min-w-0">
          <div>
            <span className="text-xs text-gray-400 font-medium">{data?.unit}</span>
            <h2
              onClick={() => navigate(`/product/${data.slug}`)}
              className="text-sm sm:text-base font-semibold text-gray-800 mt-0.5 cursor-pointer hover:text-emerald-600 transition-colors line-clamp-2"
            >
              {data?.name}
            </h2>
            {data?.description && (
              <p className="text-xs text-gray-500 mt-1 line-clamp-2">{data.description}</p>
            )}
          </div>

          <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
            {/* Price */}
            <div className="flex items-baseline gap-2">
              <span className="text-base sm:text-lg font-bold text-gray-900">
                {currencySymbol} {data?.offer_price ?? data?.price}
              </span>
              {data?.offer_price && (
                <del className="text-sm text-gray-400">{currencySymbol} {data?.price}</del>
              )}
            </div>

            {/* Cart Controls */}
            {CartItem && !data.has_variants ? (
              <div className="flex items-center gap-2 bg-emerald-500 rounded-lg px-3 py-1.5">
                <button onClick={handleDecrease} className="text-white leading-none flex items-center" aria-label={CartItem.cartQuantity === 1 ? "Remove" : "Decrease"}>
                  {CartItem.cartQuantity === 1 ? (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  ) : (
                    <span className="text-lg font-bold leading-none">−</span>
                  )}
                </button>
                <span className="text-white text-sm font-semibold min-w-[20px] text-center">{CartItem.cartQuantity}</span>
                <button onClick={() => dispatch(incrementCart(data))} className="text-white font-bold text-lg leading-none">+</button>
              </div>
            ) : (
              <button
                disabled={data?.stock_quantity === 0}
                onClick={handleAddToCart}
                className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                {t('card.addToCart')}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default CardList;
